import {CommonModule} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';
import {GateweeDocSidebarNodeComponent} from './gatewee-doc-sidebar-node.component';

export interface GateweeDocSidebarItem {
  id?: string;
  title: string;
  icon?: string;
  route?: string;
  assetPath?: string;
  confluencePageId?: string;
  subMenu?: GateweeDocSidebarItem[];
}

/** Matches a leading emoji sequence (including ZWJ compounds). */
// eslint-disable-next-line @typescript-eslint/naming-convention
const LEADING_EMOJI_RE =
  /^(\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?)*)\s*/u;

function stripLeadingEmojiFromTitle(title: string): string {
  let result = title;
  while (LEADING_EMOJI_RE.test(result)) {
    result = result.replace(LEADING_EMOJI_RE, '');
  }
  return result;
}

/** Strips leading emoji and `"N - "` prefix from Confluence page titles. */
export function formatDocSidebarTitle(title: string): string {
  const withoutEmoji = stripLeadingEmojiFromTitle(title);
  const parts = withoutEmoji.split(/\s*-\s+/, 2);
  return parts.length > 1 ? parts[1].trim() : withoutEmoji.trim();
}

@Component({
  selector: 'gatewee-doc-sidebar',
  standalone: true,
  imports: [CommonModule, GateweeIconComponent, GateweeDocSidebarNodeComponent],
  templateUrl: './gatewee-doc-sidebar.component.html',
  styleUrls: ['./gatewee-doc-sidebar.component.css'],
})
export class GateweeDocSidebarComponent implements OnChanges {
  @Input() items: GateweeDocSidebarItem[] = [];
  @Input() activePageId: string | null = null;
  @Input() isCollapsed = false;

  @Output() navigationToggle = new EventEmitter<boolean>();
  @Output() closeMobile = new EventEmitter<void>();

  readonly expandedIds = new Set<string>();
  readonly formatTitle = formatDocSidebarTitle;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['activePageId']) {
      this.syncExpandedFromActivePage();
    }
  }

  toggleCollapse(): void {
    this.navigationToggle.emit(!this.isCollapsed);
  }

  isExpanded(item: GateweeDocSidebarItem): boolean {
    return !!item.id && this.expandedIds.has(item.id);
  }

  toggleExpand(item: GateweeDocSidebarItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!item.id) {
      return;
    }
    if (this.expandedIds.has(item.id)) {
      this.expandedIds.delete(item.id);
    } else {
      this.expandedIds.add(item.id);
    }
  }

  isActiveItem(item: GateweeDocSidebarItem): boolean {
    if (!this.activePageId || !item.route) {
      return false;
    }
    return (
      item.id === this.activePageId ||
      item.route === `/documentation/${this.activePageId}` ||
      item.confluencePageId === this.activePageId
    );
  }

  isBranchActive(item: GateweeDocSidebarItem): boolean {
    if (this.isActiveItem(item)) {
      return true;
    }
    return !!item.subMenu?.some(
      child => this.isActiveItem(child) || this.isBranchActive(child),
    );
  }

  onLeafClick(): void {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.closeMobile.emit();
    }
  }

  itemIcon(item: GateweeDocSidebarItem): string {
    if (item.icon?.startsWith('@phi.')) {
      return item.icon;
    }
    return item.subMenu?.length ? '@phi.folder' : '@phi.file-text';
  }

  private syncExpandedFromActivePage(): void {
    this.expandedIds.clear();
    if (!this.activePageId) {
      return;
    }
    this.expandPathTo(this.activePageId, this.items, []);
  }

  private expandPathTo(
    pageId: string,
    nodes: GateweeDocSidebarItem[],
    ancestorIds: string[],
  ): boolean {
    for (const node of nodes) {
      if (this.isActiveItem(node)) {
        ancestorIds.forEach(id => this.expandedIds.add(id));
        return true;
      }

      if (node.subMenu?.length && node.id) {
        if (
          this.expandPathTo(pageId, node.subMenu, [...ancestorIds, node.id])
        ) {
          this.expandedIds.add(node.id);
          return true;
        }
      }
    }

    return false;
  }
}
